"use client";

import EmailPassword from "supertokens-auth-react/recipe/emailpassword";
import EmailVerification from "supertokens-auth-react/recipe/emailverification";
import Session from "supertokens-auth-react/recipe/session";
import { isSecondFactorPending } from "@workspace/core/auth/second-factor";
import {
  buildSecondFactorPath,
  buildVerifyEmailPath,
  DEFAULT_AUTHENTICATED_PATH,
  getSafeRedirectTarget,
} from "./redirect";
import {
  bindSessionIdentity,
  invalidateSessionIdentity,
  verifySessionIdentity,
} from "./session-identity";

export async function signInWithIsolatedSession(
  email: string,
  password: string,
): ReturnType<typeof EmailPassword.signIn> {
  // Revoke A before creating B. A failed sign-out must never proceed to sign-in.
  invalidateSessionIdentity(true);
  await Session.signOut({ userContext: { suppressAuthRedirect: true } });
  return EmailPassword.signIn({
    formFields: [
      { id: "email", value: email.trim().toLowerCase() },
      { id: "password", value: password },
    ],
  });
}

/**
 * Recupera uma sessao que ja existia antes de abrir a pagina de login.
 *
 * A presenca do cookie nao basta para liberar a navegacao: o destino so e
 * retornado depois da mesma validacao de identidade usada no login explicito.
 */
export async function getExistingSessionDestination(
  redirectTo = DEFAULT_AUTHENTICATED_PATH,
  allowedOrigins: readonly string[] = [],
): Promise<string | null> {
  if (!(await Session.doesSessionExist())) {
    return null;
  }

  const userId = await Session.getUserId();
  return getVerifiedLoginDestination(userId, redirectTo, allowedOrigins);
}

export async function getVerifiedLoginDestination(
  expectedUserId: string,
  redirectTo = DEFAULT_AUTHENTICATED_PATH,
  allowedOrigins: readonly string[] = [],
) {
  try {
    if (
      !(await Session.doesSessionExist()) ||
      (await Session.getUserId()) !== expectedUserId
    ) {
      throw new Error("SESSION_USER_MISMATCH");
    }
    // This endpoint confirms identity even if email verification is pending.
    await verifySessionIdentity(expectedUserId);
    const verification = await EmailVerification.isEmailVerified();
    // Remember the verified identity across the email-verification navigation.
    // This binding grants no account access; backend claim checks still apply.
    bindSessionIdentity(expectedUserId);
    const destination = getSafeRedirectTarget(redirectTo, { allowedOrigins });
    // O codigo do segundo fator vem antes da verificacao de e-mail: a tela do
    // codigo devolve ao destino, e o destino cobra o e-mail se ainda faltar.
    if (isSecondFactorPending(await Session.getAccessTokenPayloadSecurely()))
      return buildSecondFactorPath(destination, allowedOrigins);
    if (!verification.isVerified)
      return buildVerifyEmailPath(destination, allowedOrigins);
    return destination;
  } catch (error) {
    invalidateSessionIdentity(true);
    await Session.signOut({ userContext: { suppressAuthRedirect: true } });
    throw error;
  }
}
